import type {
  RawData,
  TestDefinition,
  TestResult,
  ValidationResult,
} from '../types.ts';
import {
  errorText,
  formatError,
  result,
  validation,
  validationError,
} from './shared.ts';

function webGlContexts(): {
  one: WebGLRenderingContext | null;
  two: WebGL2RenderingContext | null;
} {
  const oneCanvas = document.createElement('canvas');
  const twoCanvas = document.createElement('canvas');
  return {
    one: oneCanvas.getContext('webgl'),
    two: twoCanvas.getContext('webgl2'),
  };
}

function glVendor(
  context: WebGLRenderingContext | WebGL2RenderingContext | null,
): { masked: string; unmasked: string } {
  if (!context) {
    return { masked: '', unmasked: '' };
  }
  const extension = context.getExtension('WEBGL_debug_renderer_info');
  return {
    masked: String(context.getParameter(context.VENDOR)),
    unmasked: extension
      ? String(context.getParameter(extension.UNMASKED_VENDOR_WEBGL))
      : '',
  };
}

async function collect(): Promise<RawData> {
  const contexts = webGlContexts();
  const one = glVendor(contexts.one);
  const two = glVendor(contexts.two);
  return {
    webgl1Masked: one.masked,
    webgl1Unmasked: one.unmasked,
    webgl2Masked: two.masked,
    webgl2Unmasked: two.unmasked,
  };
}

function validateWebGlVendor(rawData: RawData): ValidationResult {
  const reasons: string[] = [];
  const one = String(rawData.webgl1Unmasked ?? '');
  const two = String(rawData.webgl2Unmasked ?? '');
  if (!one || !two) {
    reasons.push('WebGL unmasked vendor is unavailable');
  }
  if (one != two) {
    reasons.push('WebGL 1 and WebGL 2 vendors differ');
  }
  if (!/Google Inc\. \(NVIDIA\)/i.test(one)) {
    reasons.push(
      'WebGL vendor does not match the NVIDIA Google Chrome baseline',
    );
  }
  return validation(reasons);
}

function format(rawData: RawData): string {
  const error = formatError(rawData);
  if (error) {
    return error;
  }
  return String(
    rawData.webgl2Unmasked || rawData.webgl1Unmasked || 'Unavailable',
  );
}

async function run(): Promise<TestResult> {
  let rawData: RawData;
  try {
    rawData = await collect();
  } catch (error) {
    rawData = { error: errorText(error) };
  }
  return result(rawData, format);
}

function validate(rawData: RawData): ValidationResult {
  const error = validationError(rawData);
  if (error) {
    return error;
  }
  return validateWebGlVendor(rawData);
}

export const webGlVendorTest: TestDefinition = {
  id: 'webglVendor',
  title: 'WebGL vendor',
  category: 'shared',
  format,
  run,
  validate,
};
